import React, { useEffect, useMemo, useState } from 'react';
import { PlayerSpriteManifestEntry } from '../types/character';

interface Props {
  category: string;
  slotLabel: string;
  onSelect: (url: string) => void;
  onClose: () => void;
}

let manifestCache: PlayerSpriteManifestEntry[] | null = null;

const PlayerEquipSpritePicker: React.FC<Props> = ({ category, slotLabel, onSelect, onClose }) => {
  const [entries, setEntries] = useState<PlayerSpriteManifestEntry[]>(manifestCache ?? []);
  const [isLoading, setIsLoading] = useState(!manifestCache);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (manifestCache) return;
    (async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.PUBLIC_URL || ''}/sprites/player-sprite-manifest.json`);
        if (!response.ok) throw new Error('Failed to load sprite manifest');
        const data: PlayerSpriteManifestEntry[] = await response.json();
        manifestCache = data;
        setEntries(data);
      } catch {
        setError('Could not load the sprite library. Try again later.');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return entries
      .filter(e => e.category === category)
      .filter(e => !term || e.name.toLowerCase().includes(term))
      .slice(0, 300);
  }, [entries, search, category]);

  return (
    <div className="modal-overlay sprite-picker-overlay" onClick={onClose}>
      <div className="modal-content sprite-picker-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>🖼️ Choose {slotLabel} Sprite</h3>
          <button onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="sprite-picker-controls">
            <input
              type="text"
              className="sprite-picker-search"
              placeholder={`🔍 Search ${slotLabel.toLowerCase()} sprites...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          {isLoading && <p style={{ color: '#aaa' }}>Loading sprite library...</p>}
          {error && <p style={{ color: '#e94560' }}>{error}</p>}

          {!isLoading && !error && (
            <>
              <div className="sprite-picker-grid">
                {filtered.map(entry => (
                  <button
                    key={entry.url}
                    type="button"
                    className="sprite-picker-item"
                    title={entry.name}
                    onClick={() => { onSelect(entry.url); onClose(); }}
                  >
                    <img src={entry.url} alt={entry.name} loading="lazy" />
                    <span>{entry.name}</span>
                  </button>
                ))}
              </div>
              {filtered.length === 0 && <p style={{ color: '#aaa' }}>No sprites match your search.</p>}
              {filtered.length === 300 && (
                <p style={{ color: '#888', fontSize: '0.85rem' }}>Showing first 300 results - refine your search for more.</p>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default PlayerEquipSpritePicker;
